import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  TextField,
  InputAdornment,
  useTheme,
  Chip
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';
import FolderIcon from '@mui/icons-material/Folder';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import InventoryIcon from '@mui/icons-material/Inventory';
import SearchIcon from '@mui/icons-material/Search';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

// Create a new EntityContext
export const EntityContext = React.createContext({
  currentEntity: null,
  setCurrentEntity: () => {},
  entityPermissions: {},
  entities: [],
  entitiesLoading: false,
  entitiesError: null,
  fetchEntities: () => {},
  loading: false,
  error: null,
  isInitializing: true
});

// Hook to use the entity context
export const useEntityContext = () => React.useContext(EntityContext);

// EntityContextProvider component
// eslint-disable-next-line max-lines-per-function
export const EntityContextProvider = ({ children }) => {
  const { user, isLoading: authLoading } = useAuth();
  const [currentEntity, setCurrentEntity] = useState(null);
  const [entityPermissions, setEntityPermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [entities, setEntities] = useState([]);
  const [entitiesLoading, setEntitiesLoading] = useState(false);
  const [entitiesError, setEntitiesError] = useState(null);
  const [hasRestoredFromStorage, setHasRestoredFromStorage] = useState(false);

  // Centralized fetch entities function
  const fetchEntities = useCallback(async (search = '', type = '') => {
    if (!user) return;

    setEntitiesLoading(true);
    setEntitiesError(null);
    try {
      const params = {};
      if (search) params.search = search;
      if (type) params.type = type;

      const response = await api.getEntities(params);
      const entitiesArray = response?.entities || [];
      setEntities(entitiesArray);

      // Don't auto-select any entity - let user choose or restore from localStorage

      return entitiesArray;
    } catch (err) {
      console.error('Error fetching entities:', err);
      setEntitiesError('Failed to load entities');
      return [];
    } finally {
      setEntitiesLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, setCurrentEntity]);

  // Fetch entities on user change
  useEffect(() => {
    if (user) {
      fetchEntities();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Load permissions for the current entity
  useEffect(() => {
    if (!currentEntity || !user) return;

    const fetchEntityPermissions = async () => {
      setLoading(true);
      setError(null);
      try {
        // For now, we'll use the same permissions structure
        // In the future, this could be updated to entity-specific permissions
        const response = await api.getClientPermissions(currentEntity.id);
        setEntityPermissions(response.permissions || {});
      } catch (err) {
        console.error('Error fetching entity permissions:', err);
        setError('Failed to load permissions');
      } finally {
        setLoading(false);
      }
    };

    fetchEntityPermissions();
  }, [currentEntity, user]);

  // Try to restore the last selected entity from localStorage
  useEffect(() => {
    // Don't try to restore until auth is ready and we haven't already restored
    if (authLoading || hasRestoredFromStorage || !user) return;

    try {
      const savedEntity = localStorage.getItem(`lastEntity_${user.uid}`);
      if (savedEntity) {
        const entityData = JSON.parse(savedEntity);
        setCurrentEntity(entityData);
      }
      setHasRestoredFromStorage(true);
    } catch (e) {
      console.error('Failed to restore entity from localStorage:', e);
      setHasRestoredFromStorage(true);
    }
  }, [user, authLoading, hasRestoredFromStorage]);

  // Validate restored entity still exists when entities are first loaded
  const hasValidatedEntity = React.useRef(false);
  useEffect(() => {
    // Only validate once when entities first load (not on every re-fetch)
    if (!currentEntity || !entities.length || hasValidatedEntity.current) return;

    hasValidatedEntity.current = true;
    const entityStillExists = entities.some(e => e.id === currentEntity.id);
    if (!entityStillExists) {
      setCurrentEntity(null);
      localStorage.removeItem(`lastEntity_${user?.uid}`);
    }
  }, [entities, currentEntity, user]);

  // Save the selected entity to localStorage
  useEffect(() => {
    if (!user || !currentEntity) return;

    try {
      localStorage.setItem(
        `lastEntity_${user.uid}`,
        JSON.stringify(currentEntity)
      );
    } catch (e) {
      console.error('Failed to save entity to localStorage:', e);
    }
  }, [currentEntity, user]);

  // Determine if we're still initializing (waiting for auth or restoration)
  const isInitializing = authLoading || (!hasRestoredFromStorage && !!user);

  return (
    <EntityContext.Provider
      value={{
        currentEntity,
        setCurrentEntity,
        entityPermissions,
        entities,
        entitiesLoading,
        entitiesError,
        fetchEntities,
        loading,
        error,
        isInitializing
      }}
    >
      {children}
    </EntityContext.Provider>
  );
};

// Get entity icon based on type
const getEntityIcon = (type) => {
  switch (type) {
    case 'ORGANIZATION':
      return <BusinessIcon />;
    case 'PERSON':
      return <PersonIcon />;
    case 'EVENT':
      return <EventIcon />;
    case 'PROJECT':
      return <FolderIcon />;
    case 'PROGRAM':
      return <AccountTreeIcon />;
    case 'RESOURCE':
      return <InventoryIcon />;
    default:
      return <BusinessIcon />;
  }
};

// Get type color
const getTypeColor = (type) => {
  switch (type) {
    case 'ORGANIZATION':
      return 'primary';
    case 'PERSON':
      return 'secondary';
    case 'PROJECT':
      return 'info';
    case 'PROGRAM':
      return 'success';
    case 'EVENT':
      return 'warning';
    case 'RESOURCE':
      return 'error';
    default:
      return 'default';
  }
};

// Main EntityContextSwitch component
// eslint-disable-next-line max-lines-per-function
const EntityContextSwitch = ({ onEntitySelect, compact = false }) => {
  const {
    currentEntity,
    setCurrentEntity,
    entities,
    entitiesLoading,
    entitiesError,
    fetchEntities
  } = useEntityContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const { user } = useAuth();
  const theme = useTheme();

  // Handle search with debouncing
  useEffect(() => {
    if (!user) return;

    const timer = setTimeout(() => {
      fetchEntities(searchTerm, typeFilter);
    }, 300); // 300ms debounce

    return () => {
      clearTimeout(timer);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, typeFilter, fetchEntities]);

  // No need for client-side filtering anymore
  const filteredEntities = entities;

  // Handle entity selection
  const handleSelectEntity = (entity) => {
    setCurrentEntity(entity);
    onEntitySelect?.(entity);
  };

  // Unified entity selector for all screen sizes
  return (
    <Box sx={{ minWidth: compact ? 0 : 200, maxWidth: compact ? '100%' : 300, width: compact ? '100%' : 'auto' }}>
      <FormControl fullWidth variant="outlined" size="small">
        <InputLabel id="entity-select-label"
          sx={{
            fontWeight: 600,
            backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50],
            px: 1,
            color: theme.palette.mode === 'dark' ? theme.palette.grey[100] : theme.palette.grey[900]
          }}
        >
          Select Entity
        </InputLabel>
        <Select
          labelId="entity-select-label"
          id="entity-select"
          value={entities.some(e => e.id === currentEntity?.id) ? currentEntity.id : ''}
          label="Select Entity"
          onChange={(e) => {
            const entityId = e.target.value;
            if (!entityId) return;
            const selectedEntity = Array.isArray(entities)
              ? entities.find(c => c.id === entityId)
              : null;
            if (selectedEntity) {
              handleSelectEntity(selectedEntity);
            }
          }}
          sx={{
            backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50],
            color: theme.palette.mode === 'dark' ? theme.palette.grey[100] : theme.palette.grey[900],
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
            },
            '.MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300],
              borderWidth: 2
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
              borderWidth: 2
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[500] : theme.palette.grey[500],
              borderWidth: 2
            },
            '.MuiSvgIcon-root': {
              color: theme.palette.mode === 'dark' ? theme.palette.grey[100] : theme.palette.grey[900],
            }
          }}
          renderValue={(selected) => {
            const selectedEntity = Array.isArray(entities)
              ? entities.find(c => c.id === selected)
              : null;
            if (!selectedEntity) return 'Select Entity';

            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: compact ? 0.5 : 1, minWidth: 0 }}>
                {getEntityIcon(selectedEntity.type)}
                <Typography
                  variant="body2"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    minWidth: 0,
                    fontSize: compact ? '0.8rem' : undefined
                  }}
                >
                  {selectedEntity.name}
                </Typography>
                {!compact && (
                  <Chip
                    label={selectedEntity.type}
                    size="small"
                    color={getTypeColor(selectedEntity.type)}
                  />
                )}
              </Box>
            );
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: 450,
                backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : '#ffffff',
                border: `2px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300]}`,
                boxShadow: theme.shadows[8],
                '& .MuiMenuItem-root': {
                  color: theme.palette.mode === 'dark' ? theme.palette.grey[100] : theme.palette.grey[900],
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100]
                  },
                  '&.Mui-selected': {
                    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.primary.dark : theme.palette.primary.light,
                    color: theme.palette.mode === 'dark' ? theme.palette.primary.contrastText : theme.palette.primary.main,
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'dark' ? theme.palette.primary.dark : theme.palette.primary.light
                    }
                  }
                },
                '& .MuiListItemText-primary': {
                  color: 'inherit',
                  fontWeight: 500
                },
                '& .MuiListItemText-secondary': {
                  color: theme.palette.mode === 'dark' ? theme.palette.grey[400] : theme.palette.grey[600],
                  fontSize: '0.75rem'
                },
                '& .MuiListItemIcon-root': {
                  color: 'inherit',
                  minWidth: 40
                }
              }
            }
          }}
        >
          <MenuItem
            value=""
            sx={{
              position: 'sticky',
              top: 0,
              bgcolor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : '#ffffff',
              borderBottom: `2px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300]}`,
              zIndex: 1,
              pb: 2,
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : '#ffffff'
              }
            }}
          >
            <Box sx={{ width: '100%' }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search entities..."
                variant="outlined"
                value={searchTerm}
                onChange={(e) => {
                  e.stopPropagation();
                  setSearchTerm(e.target.value);
                }}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
                    </InputAdornment>
                  ),
                  sx: {
                    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50],
                    '& input': {
                      color: theme.palette.mode === 'dark' ? theme.palette.grey[100] : theme.palette.grey[900]
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[400],
                      borderWidth: 2
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[500] : theme.palette.grey[600],
                      borderWidth: 2
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.main,
                      borderWidth: 2
                    }
                  }
                }}
                sx={{ mb: 1 }}
              />

              {/* Type filter */}
              <FormControl fullWidth size="small">
                <InputLabel id="type-filter-label">Filter by Type</InputLabel>
                <Select
                  labelId="type-filter-label"
                  value={typeFilter}
                  label="Filter by Type"
                  onChange={(e) => {
                    e.stopPropagation();
                    setTypeFilter(e.target.value);
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="ORGANIZATION">Organization</MenuItem>
                  <MenuItem value="PERSON">Person</MenuItem>
                  <MenuItem value="PROJECT">Project</MenuItem>
                  <MenuItem value="PROGRAM">Program</MenuItem>
                  <MenuItem value="EVENT">Event</MenuItem>
                  <MenuItem value="RESOURCE">Resource</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </MenuItem>

          {entitiesLoading ? (
            <MenuItem disabled>
              <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', py: 1 }}>
                <CircularProgress size={24} />
              </Box>
            </MenuItem>
          ) : entitiesError ? (
            <MenuItem disabled>
              <Typography color="error" variant="body2">
                {entitiesError}
              </Typography>
            </MenuItem>
          ) : filteredEntities.length === 0 ? (
            <MenuItem disabled>
              <Typography variant="body2">No entities found</Typography>
            </MenuItem>
          ) : (
            filteredEntities.map((entity) => (
              <MenuItem key={entity.id} value={entity.id}>
                <ListItemIcon>
                  {getEntityIcon(entity.type)}
                </ListItemIcon>
                <ListItemText
                  primaryTypographyProps={{ component: 'div', variant: 'body2' }}
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {entity.name}
                      <Chip
                        label={entity.type}
                        size="small"
                        color={getTypeColor(entity.type)}
                      />
                    </Box>
                  }
                  secondary={entity.id}
                  secondaryTypographyProps={{
                    variant: 'caption'
                  }}
                />
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>
    </Box>
  );
};

export default EntityContextSwitch;